import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Switch, Button, Space, message, DatePicker, Typography, Divider } from 'antd';
import { SaveOutlined, SendOutlined, CalendarOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
const { TextArea } = Input;
const { Text } = Typography;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
  
  .ant-modal-header {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-modal-footer {
    background: ${COLORS.bgSecondary} !important;
    border-top: 1px solid ${COLORS.border} !important;
  }
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  
  .section-title {
    color: ${COLORS.textPrimary};
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

interface BirthdayEmailConfigModalProps {
  visible: boolean;
  onClose: () => void;
}

const BirthdayEmailConfigModal: React.FC<BirthdayEmailConfigModalProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_BIRTHDAY_EMAIL_SETTINGS);
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        form.setFieldsValue({
          enabled: data.enabled,
          subjectForAdmins: data.subjectForAdmins,
          subjectForMerchants: data.subjectForMerchants,
          messageForAdmins: data.messageForAdmins,
          messageForMerchants: data.messageForMerchants,
        });
      }
    } catch (error: any) {
      message.error('Failed to fetch birthday email settings');
      console.error('Error fetching settings:', error);
    }
  }, [form]);

  useEffect(() => {
    if (visible) {
      fetchSettings();
    }
  }, [visible, fetchSettings]);

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      await apiClient.put(API_ENDPOINTS.ADMIN_UPDATE_BIRTHDAY_EMAIL_SETTINGS, {
        enabled: values.enabled,
        subjectForAdmins: values.subjectForAdmins,
        subjectForMerchants: values.subjectForMerchants,
        messageForAdmins: values.messageForAdmins,
        messageForMerchants: values.messageForMerchants,
      });
      message.success('Birthday email settings updated successfully');
      await fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNow = async () => {
    const values = form.getFieldsValue();
    const targetDate = values.targetDate ? values.targetDate.format('YYYY-MM-DD') : undefined;
    
    setSending(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_SEND_BIRTHDAY_EMAILS_NOW, {
        targetDate,
      });
      if (response.data?.status === 'success') {
        const data = response.data.data;
        message.success(`Birthday emails sent: ${data.success} success, ${data.failed} failed`);
        if (data.errors && data.errors.length > 0) {
          console.error('Email errors:', data.errors);
        }
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to send birthday emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <StyledModal
      title="Configure Birthday Messages"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      style={{ top: 20 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          enabled: true,
        }}
      >
        <FormSection>
          <div className="section-title">
            <CalendarOutlined /> General Settings
          </div>
          <Form.Item
            name="enabled"
            label="Enable Birthday Emails"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginTop: -12, marginBottom: 16 }}>
            When enabled, birthday emails will be sent automatically to administrators and merchants on their birthdays.
          </Text>
        </FormSection>

        <Divider style={{ borderColor: COLORS.border }} />

        <FormSection>
          <div className="section-title">Administrator Email Template</div>
          <Form.Item
            name="subjectForAdmins"
            label="Subject"
            rules={[{ required: true, message: 'Please enter subject' }]}
            tooltip="Use [fullName] placeholder to insert the recipient's full name"
          >
            <Input placeholder="Happy Birthday, [fullName]! 🎉" />
          </Form.Item>
          <Form.Item
            name="messageForAdmins"
            label="Message"
            rules={[{ required: true, message: 'Please enter message' }]}
            tooltip="Use [fullName] placeholder to insert the recipient's full name"
          >
            <TextArea
              rows={6}
              placeholder="Dear [fullName],&#10;&#10;We want to take a moment to wish you a very happy birthday! 🎂🎈&#10;&#10;Thank you for being an integral part of the SwitchWallet team..."
            />
          </Form.Item>
        </FormSection>

        <Divider style={{ borderColor: COLORS.border }} />

        <FormSection>
          <div className="section-title">Merchant Email Template</div>
          <Form.Item
            name="subjectForMerchants"
            label="Subject"
            rules={[{ required: true, message: 'Please enter subject' }]}
            tooltip="Use [fullName] placeholder to insert the recipient's full name"
          >
            <Input placeholder="Happy Birthday, [fullName]! 🎉" />
          </Form.Item>
          <Form.Item
            name="messageForMerchants"
            label="Message"
            rules={[{ required: true, message: 'Please enter message' }]}
            tooltip="Use [fullName] placeholder to insert the recipient's full name"
          >
            <TextArea
              rows={6}
              placeholder="Dear [fullName],&#10;&#10;We want to take a moment to wish you a very happy birthday! 🎂🎈&#10;&#10;Thank you for choosing SwitchWallet..."
            />
          </Form.Item>
        </FormSection>

        <Divider style={{ borderColor: COLORS.border }} />

        <FormSection>
          <div className="section-title">Send Birthday Emails Now</div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Manually trigger birthday emails for a specific date. Leave empty to send for today.
          </Text>
          <Form.Item
            name="targetDate"
            label="Target Date (optional)"
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              placeholder="Select date (leave empty for today)"
            />
          </Form.Item>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendNow}
            loading={sending}
            style={{ width: '100%' }}
          >
            Send Birthday Emails Now
          </Button>
        </FormSection>

        <Divider style={{ borderColor: COLORS.border }} />

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
              Save Settings
            </Button>
            <Button onClick={onClose}>
              Close
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </StyledModal>
  );
};

export default BirthdayEmailConfigModal;

